import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm, FieldValues } from 'react-hook-form';
import { ImScissors } from 'react-icons/im';
import { MdKeyboardBackspace } from 'react-icons/md';
import { OptionTypeBase } from 'react-select';
import axios from 'axios';
import FileDownload from 'js-file-download';

import { onDelay } from 'utils/debounce';

import { IOptionReactSelect } from 'shared/interfaces';

import { getCostureiras } from 'services/api/fornecedor';
import { getProdutos } from 'services/api/produto';
import { getRelatorioRecebimentoCorte } from 'services/api/cortesRecebimento';

import { useToast } from 'contexts';
import * as C from 'components';

import { saldoRecebimentoCorteOption } from './options';

import * as S from './styles';

import { IRelatorioRecebimentoCorteForm } from '../interfaces';

export const RecebimentoCortePrint: React.FC = () => {
  const { addToast } = useToast();

  const currentDate = new Date();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
  } = useForm<FieldValues>({
    defaultValues: {
      saldo: { label: 'FECHADO', value: 'F' },
      data_recebimento_inicial: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1,
      ),
      data_recebimento_final: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0,
      ),
    },
  });

  const [loading, setLoading] = useState(false);

  const loadOptionsProdutos = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getProdutos({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const loadOptionsFornecedores = onDelay(
    (
      inputValue: string,
      callback: (options: OptionTypeBase[]) => void,
    ): void => {
      getCostureiras({
        page: 1,
        per_page: 15,
        term: inputValue,
      }).then((response) => {
        const values: IOptionReactSelect[] = response.data.map((item) => {
          return {
            label: item.nome,
            value: item.id,
          };
        });

        return callback(values);
      });
    },
    500,
  );

  const onSubmit = useCallback(
    async (data: IRelatorioRecebimentoCorteForm) => {
      try {
        setLoading(true);

        const response = await getRelatorioRecebimentoCorte(data);

        if (response.link) {
          axios({
            url: `${response.link}`,
            method: 'GET',
            responseType: 'blob',
          }).then((relatorio) => {
            FileDownload(relatorio.data, 'Recebimento corte.pdf');
          });
        }

        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Salvo com sucesso',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Ops, Erro',
          description: 'Ocorreu um erro ao inserir um novo registro',
        });
      } finally {
        setLoading(false);
      }
    },

    [addToast],
  );

  return (
    <S.Container>
      <C.HeaderBackground>
        <S.HeaderPageDatail>
          <S.BackLink>
            <Link to="/recebimento-corte">
              <MdKeyboardBackspace size={24} color="#fff" />
              Recebimento corte
            </Link>
          </S.BackLink>

          <C.HeaderInfo
            title="Relatorios recebimento de corte"
            icon={ImScissors}
          />
        </S.HeaderPageDatail>
      </C.HeaderBackground>

      <S.Panel>
        <form onSubmit={handleSubmit(onSubmit)}>
          <S.FormContainer>
            <C.FormGroup>
              <C.Label>Data recebimento inicial</C.Label>
              <C.InputDatePicker
                {...register('data_recebimento_inicial')}
                control={control}
                errors={errors.data_recebimento_inicial}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_recebimento_inicial')}
                name="data_recebimento_inicial"
                placeholder="Data recebimento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Data recebimento final</C.Label>
              <C.InputDatePicker
                {...register('data_recebimento_final')}
                control={control}
                errors={errors.data_recebimento_final}
                dateFormat="dd/MM/yyyy"
                selected={watch('data_recebimento_final')}
                name="data_recebimento_final"
                placeholder="Data recebimento"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Costureira</C.Label>
              <C.ReactSelectAsync
                {...register('fornecedor_id')}
                control={control}
                errors={errors.fornecedor_id}
                loadOptions={loadOptionsFornecedores}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhum costureira localizada"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Produto</C.Label>
              <C.ReactSelectAsync
                {...register('produto_id')}
                control={control}
                errors={errors.produto_id}
                loadOptions={loadOptionsProdutos}
                isClearable
                isSearchable
                loadingMessage="Buscando..."
                noOptionsMessage="Nenhum produto localizado"
              />
            </C.FormGroup>

            <C.FormGroup>
              <C.Label>Status saldo</C.Label>
              <C.ReactSelect
                {...register('saldo')}
                name="saldo"
                control={control}
                errors={errors.status}
                options={saldoRecebimentoCorteOption}
              />
            </C.FormGroup>
          </S.FormContainer>

          <C.Button
            variant="primary"
            disabled={loading}
            loading={loading}
            type="submit"
            title="Gerar relatorio"
          >
            Gerar relatório
          </C.Button>
        </form>
      </S.Panel>
    </S.Container>
  );
};
